import React, { useCallback, useEffect, useState } from "react"
import {
  Badge,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Table,
  UncontrolledDropdown,
} from "reactstrap"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import LocalizedDatePicker from "components/Common/LocalizedDatePicker"
import Paginations from "components/Common/Paginations"
import DownloadCSV from "components/Shared/DownloadCSV"
import DownloadExcel from "components/Shared/DownloadExcel"
import formatDateTime from "components/Shared/formatDateTime"
import {
  getPartnerDailySummary,
  getPartnerDetails,
} from "services/deliveryPartnerService"
import { getPartnerBranchName, getPartnerProfilePhoto } from "./branchUtils"
import { getRiderAvailabilityDisplay } from "utils/riderAvailability"
import { formatCurrencyAmount } from "utils/currency"
import useAllowExports from "hooks/useAllowExports"
import LoadingSpinner from "./LoadingSpinner"
import EmptyState from "./EmptyState"

const DATE_VALUE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const formatToday = () => {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate()
  ).padStart(2, "0")}`
}

const isValidDateString = value =>
  typeof value === "string" && DATE_VALUE_PATTERN.test(value)

const isFutureDate = date => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const candidate = new Date(date)
  candidate.setHours(0, 0, 0, 0)
  return candidate.getTime() > today.getTime()
}

const isFutureDateString = value => isValidDateString(value) && value > formatToday()

const normalizeStatusKey = status => {
  if (status === null || status === undefined) return ""
  return String(status).toLowerCase()
}

const partnerStatusColor = status => {
  switch (normalizeStatusKey(status)) {
    case "approved":
      return "success"
    case "pending":
      return "warning"
    case "suspended":
      return "danger"
    case "rejected":
      return "secondary"
    default:
      return "light"
  }
}

const deliveryStatusColor = status => {
  switch (normalizeStatusKey(status)) {
    case "assigned":
    case "accepted":
    case "created":
      return "primary"
    case "unassigned":
      return "secondary"
    case "in_progress":
    case "arrivedpickup":
    case "pickedup":
    case "outfordelivery":
    case "arriveddrop":
      return "warning"
    case "delivered":
      return "success"
    case "cancelled":
    case "failed":
      return "danger"
    default:
      return "light"
  }
}

const paymentStatusColor = status => {
  switch (Number(status)) {
    case 0:
      return "warning"
    case 1:
      return "success"
    case 2:
      return "info"
    default:
      return "secondary"
  }
}

const translateStatus = (status, t) => {
  if (status === null || status === undefined || status === "") return t("Unknown")
  if (typeof status !== "string") return String(status)
  const key = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
  return t(key, { defaultValue: status })
}

const RIDER_PROGRESS_STATUSES = [
  "Created",
  "Assigned",
  "Accepted",
  "ArrivedPickup",
  "PickedUp",
  "OutForDelivery",
  "ArrivedDrop",
  "Delivered",
  "Failed",
  "Cancelled",
]

const translateRiderProgressStatus = (status, t) => {
  const labels = {
    Created: t("Created"),
    Assigned: t("Assigned"),
    Accepted: t("Accepted"),
    ArrivedPickup: t("Arrived at Branch"),
    PickedUp: t("Picked Up"),
    OutForDelivery: t("Out for Delivery"),
    ArrivedDrop: t("Arrived at Customer"),
    Delivered: t("Delivered"),
    Failed: t("Failed"),
    Cancelled: t("Cancelled"),
  }
  if (labels[status]) return labels[status]

  const deliveryLabels = {
    unassigned: t("Not Assigned"),
    assigned: t("Assigned"),
    in_progress: t("In Progress"),
    delivered: t("Delivered"),
    cancelled: t("Cancelled"),
  }
  return deliveryLabels[normalizeStatusKey(status)] || translateStatus(status, t)
}

const resolveDeliveryStatusValue = order => {
  if (order?.status && RIDER_PROGRESS_STATUSES.includes(order.status)) {
    return order.status
  }
  const deliveryStatusFallbacks = {
    assigned: "Assigned",
    in_progress: "Accepted",
    delivered: "Delivered",
    cancelled: "Cancelled",
    unassigned: "unassigned",
  }
  if (order?.deliveryStatus && deliveryStatusFallbacks[order.deliveryStatus]) {
    return deliveryStatusFallbacks[order.deliveryStatus]
  }
  return order?.deliveryStatus || null
}

const translatePaymentStatus = (status, t) => {
  switch (Number(status)) {
    case 0:
      return t("Pending")
    case 1:
      return t("Paid")
    case 2:
      return t("Refund")
    default:
      return t("Unknown")
  }
}

const resolveOrderDate = order => order?.orderDate || order?.createdAt || null

const resolveEstimatedDeliveryDate = order =>
  order?.estimatedDeliveryDate || null

const resolveDeliveryDate = order => order?.deliveredAt || null

const formatOrderDateTime = value => {
  if (!value) return "—"
  return formatDateTime(value) || "—"
}

const formatOrderDateOnly = value => {
  if (!value) return "—"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "—"
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

const escapeCsvValue = value => {
  const text = value === null || value === undefined ? "" : String(value)
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`
  }
  return text
}

const buildExportFileName = (partner, date) => {
  const safeName = String(partner?.name || "rider")
    .trim()
    .replace(/[^\w\-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 40)
  return `rider_daily_summary_${safeName || "rider"}_${date || "date"}`
}

const DeliveryPartnerDailySummary = ({ partnerId, initialPartner = null }) => {
  const { t, i18n } = useTranslation()
  const allowExports = useAllowExports()
  const [partner, setPartner] = useState(initialPartner)
  const [detailsLoading, setDetailsLoading] = useState(!initialPartner)
  const [ordersLoading, setOrdersLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [orders, setOrders] = useState([])
  const [selectedDate, setSelectedDate] = useState(formatToday())
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    itemsPerPage: 50,
  })
  const [error, setError] = useState(null)

  const loadPartnerDetails = useCallback(async () => {
    if (!partnerId) return
    setDetailsLoading(true)
    setError(null)
    try {
      const response = await getPartnerDetails(partnerId)
      if (response?.status) {
        setPartner(response.data)
      } else {
        setError(response?.msg || t("Failed to fetch rider details"))
      }
    } catch (err) {
      setError(err?.response?.data?.msg || t("Failed to fetch rider details"))
    } finally {
      setDetailsLoading(false)
    }
  }, [partnerId, t])

  const loadDailySummary = useCallback(
    async (page = 1, date = selectedDate) => {
      if (!partnerId || !date) return
      setOrdersLoading(true)
      setError(null)
      try {
        const response = await getPartnerDailySummary(partnerId, {
          date,
          page,
          limit: pagination.itemsPerPage,
        })
        if (response?.status) {
          setOrders(Array.isArray(response.data) ? response.data : [])
          setPagination(prev => ({
            ...prev,
            currentPage: response.currentPage ?? page,
            totalPages: response.totalPages ?? 1,
            totalCount: response.totalCount ?? 0,
          }))
        } else {
          setOrders([])
          setError(response?.msg || t("Failed to fetch daily summary"))
        }
      } catch (err) {
        setOrders([])
        setError(err?.response?.data?.msg || t("Failed to fetch daily summary"))
      } finally {
        setOrdersLoading(false)
      }
    },
    [partnerId, selectedDate, pagination.itemsPerPage, t]
  )

  useEffect(() => {
    if (initialPartner) {
      setPartner(initialPartner)
      setDetailsLoading(false)
    }
    loadPartnerDetails()
  }, [partnerId, initialPartner, loadPartnerDetails])

  useEffect(() => {
    loadDailySummary(1, selectedDate)
  }, [loadDailySummary, selectedDate])

  const handleDateChange = value => {
    if (!isValidDateString(value) || isFutureDateString(value)) return
    setSelectedDate(value)
    setPagination(prev => ({ ...prev, currentPage: 1 }))
  }

  const handlePageChange = page => {
    setPagination(prev => ({ ...prev, currentPage: page }))
    loadDailySummary(page, selectedDate)
  }

  const buildExportRows = useCallback(
    (ordersList = []) =>
      ordersList.map((order, idx) => {
        const orderLabel = order.invoiceNo || order.orderId || order._id || "—"
        const deliveryStatusValue = resolveDeliveryStatusValue(order)
        return {
          "#": idx + 1,
          [t("Order ID")]: orderLabel,
          [t("Order Date")]: formatOrderDateTime(resolveOrderDate(order)),
          [t("Expected Delivery Date")]: formatOrderDateOnly(
            resolveEstimatedDeliveryDate(order)
          ),
          [t("Delivery Date")]: formatOrderDateTime(resolveDeliveryDate(order)),
          [t("Amount")]: formatCurrencyAmount(
            order.totalAmount ?? order.paidAmount ?? 0,
            order.splitPayments?.currency
          ),
          [t("Payment Method")]: order.paymentMethod
            ? t(order.paymentMethod)
            : "—",
          [t("Payment Status")]: translatePaymentStatus(order.paymentStatus, t),
          [t("Delivery Status")]: translateRiderProgressStatus(
            deliveryStatusValue,
            t
          ),
        }
      }),
    [t]
  )

  const fetchAllOrdersForExport = useCallback(async () => {
    if (!partnerId || !selectedDate) return []
    if (
      pagination.totalPages <= 1 &&
      Array.isArray(orders) &&
      orders.length > 0
    ) {
      return orders
    }

    const response = await getPartnerDailySummary(partnerId, {
      date: selectedDate,
      page: 1,
      limit: Math.max(pagination.totalCount || 0, orders.length, 1000),
    })
    if (!response?.status) {
      throw new Error(response?.msg || t("Failed to fetch daily summary"))
    }
    return Array.isArray(response.data) ? response.data : []
  }, [
    partnerId,
    selectedDate,
    pagination.totalPages,
    pagination.totalCount,
    orders,
    t,
  ])

  const handleExport = async type => {
    if (!allowExports || exporting) return
    setExporting(true)
    setError(null)
    try {
      const allOrders = await fetchAllOrdersForExport()
      if (!allOrders.length) {
        setError(t("No assigned orders for the selected date"))
        return
      }
      const rows = buildExportRows(allOrders)
      const fileName = buildExportFileName(partner, selectedDate)
      if (type === "xlsx") {
        DownloadExcel(rows, fileName)
      } else {
        const headers = Object.keys(rows[0])
        const csv = [
          headers.map(escapeCsvValue).join(","),
          ...rows.map(row => headers.map(header => escapeCsvValue(row[header])).join(",")),
        ].join("\n")
        DownloadCSV(csv, fileName)
      }
    } catch (err) {
      setError(err?.message || t("Failed to export daily summary"))
    } finally {
      setExporting(false)
    }
  }

  if (detailsLoading && !partner) {
    return <LoadingSpinner text={t("Loading rider details...")} />
  }

  if (!partner) {
    return (
      <EmptyState
        title={t("Rider not found")}
        subtitle={error || t("Unable to load rider details")}
      />
    )
  }

  const branchName = getPartnerBranchName(partner, i18n.language)
  const profilePhoto = getPartnerProfilePhoto(partner)

  return (
    <div>
      <Card className="shadow-sm mb-3">
        <CardBody>
          <Row className="align-items-center">
            <Col xs="auto">
              <div
                className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                style={{ width: 72, height: 72, overflow: "hidden" }}
              >
                {profilePhoto ? (
                  <img
                    src={profilePhoto}
                    alt={partner.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <i className="bx bx-user font-size-36 text-muted"></i>
                )}
              </div>
            </Col>
            <Col>
              <h5 className="mb-1">{partner.name || t("N/A")}</h5>
              <p className="mb-1 text-muted">
                <i className="bx bx-phone me-1"></i>
                {partner.phone ? (
                  <span dir="ltr" style={{ unicodeBidi: "bidi-override" }}>
                    {partner.phone}
                  </span>
                ) : (
                  t("N/A")
                )}
              </p>
              <p className="mb-1 text-muted">
                <i className="bx bx-envelope me-1"></i>
                {partner.email || t("N/A")}
              </p>
              <p className="mb-0 text-muted">
                <i className="bx bx-store me-1"></i>
                {branchName || t("N/A")}
              </p>
            </Col>
            <Col xs="auto" className="d-flex flex-column align-items-end gap-2">
              <Badge color={partnerStatusColor(partner.status)} className="font-size-13 px-3 py-2">
                {translateStatus(partner.status, t)}
              </Badge>
              {(() => {
                const availability = getRiderAvailabilityDisplay(partner, t)
                return (
                  <span className="d-inline-flex align-items-center gap-1 small">
                    <span
                      className="rounded-circle flex-shrink-0"
                      style={{
                        width: 8,
                        height: 8,
                        backgroundColor: availability.color,
                      }}
                    />
                    <span className={availability.textClass}>{availability.label}</span>
                  </span>
                )
              })()}
            </Col>
          </Row>
        </CardBody>
      </Card>

      <div className="d-flex justify-content-end align-items-end gap-2 mb-3 flex-wrap">
        <div style={{ minWidth: 180, maxWidth: 220 }}>
          <label className="form-label mb-1 small text-muted">{t("Date")}</label>
          <LocalizedDatePicker
            value={selectedDate}
            onChange={handleDateChange}
            placeholder={t("Select date")}
            cleanable={false}
            editable={false}
            shouldDisableDate={isFutureDate}
            oneTap
          />
        </div>
        {allowExports ? (
          <UncontrolledDropdown className="dropdown d-inline-block">
            <DropdownToggle
              type="menu"
              className="btn btn-success"
              id="riderDailySummaryExportDropdown"
              disabled={exporting || ordersLoading || orders.length === 0}
            >
              {exporting ? (
                <i className="bx bx-loader-alt bx-spin"></i>
              ) : (
                <i className="mdi mdi-dots-vertical"></i>
              )}
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem tag="button" onClick={() => handleExport("csv")}>
                {t("Download CSV")}
              </DropdownItem>
              <DropdownItem tag="button" onClick={() => handleExport("xlsx")}>
                {t("Download XLSX")}
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        ) : null}
      </div>

      {error ? <div className="alert alert-danger py-2">{error}</div> : null}

      {ordersLoading ? (
        <LoadingSpinner text={t("Loading daily summary...")} />
      ) : orders.length === 0 ? (
        <EmptyState
          title={t("No orders found")}
          subtitle={t("No assigned orders for the selected date")}
        />
      ) : (
        <>
          <div
            className="table-responsive border rounded"
            style={{ maxHeight: "min(50vh, 420px)", overflowY: "auto" }}
          >
            <Table className="table align-middle table-nowrap mb-0" hover>
              <thead
                className="table-light"
                style={{ position: "sticky", top: 0, zIndex: 1 }}
              >
                <tr>
                  <th>#</th>
                  <th>{t("Order ID")}</th>
                  <th>{t("Order Date")}</th>
                  <th>{t("Expected Delivery Date")}</th>
                  <th>{t("Delivery Date")}</th>
                  <th>{t("Amount")}</th>
                  <th>{t("Payment Method")}</th>
                  <th>{t("Payment Status")}</th>
                  <th>{t("Delivery Status")}</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order, idx) => {
                  const orderLabel = order.invoiceNo || order.orderId || order._id
                  const deliveryStatusValue = resolveDeliveryStatusValue(order)
                  const rowNum =
                    (pagination.currentPage - 1) * pagination.itemsPerPage + idx + 1
                  return (
                    <tr key={order._id || idx}>
                      <td>{rowNum}</td>
                      <td>
                        <Link
                          to={`/f&b-orders/order-details?id=${order._id}`}
                          className="text-primary"
                        >
                          {orderLabel}
                        </Link>
                      </td>
                      <td>{formatOrderDateTime(resolveOrderDate(order))}</td>
                      <td>
                        {formatOrderDateOnly(resolveEstimatedDeliveryDate(order))}
                      </td>
                      <td>{formatOrderDateTime(resolveDeliveryDate(order))}</td>
                      <td>
                        {formatCurrencyAmount(
                          order.totalAmount ?? order.paidAmount ?? 0,
                          order.splitPayments?.currency
                        )}
                      </td>
                      <td>{order.paymentMethod ? t(order.paymentMethod) : "—"}</td>
                      <td>
                        <Badge color={paymentStatusColor(order.paymentStatus)}>
                          {translatePaymentStatus(order.paymentStatus, t)}
                        </Badge>
                      </td>
                      <td>
                        <Badge color={deliveryStatusColor(deliveryStatusValue)}>
                          {translateRiderProgressStatus(deliveryStatusValue, t)}
                        </Badge>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
          {pagination.totalPages > 1 ? (
            <div className="mt-3">
              <Paginations
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalCount={pagination.totalCount}
                onPageChange={handlePageChange}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}

export default DeliveryPartnerDailySummary
